import { type InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ActivitiesResponse, CreatorActivity, MessageCreatorsResponse, TikTokMessage } from "@unbox/api";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// Re-export types for convenience
export type { ActivitiesResponse, CreatorActivity, TikTokMessage };

// ============================================================================
// Creator-direct messaging (no action logging)
// ============================================================================

type MessageCreatorsPayload = {
  message: string;
  creatorIds: string[];
  conversationId?: string; // Optional: if provided, use existing conversation
  imageId?: string;
};

type MessageCreatorsResponseWithActivities = MessageCreatorsResponse & {
  activities: CreatorActivity[];
};

export type MessageCreatorsResult =
  | ({ async: false } & MessageCreatorsResponseWithActivities)
  | { async: true; jobId: string };

export const useMessageCreators = () => {
  const scope = useScopedMerchantClient();
  const queryClientHook = useQueryClient();

  return useMutation({
    mutationFn: async ({
      creatorIds,
      message,
      conversationId,
      imageId,
    }: MessageCreatorsPayload): Promise<MessageCreatorsResult> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["message-creators"].$post({
            param: scope.param,
            json: { message, creatorIds, conversationId, imageId },
          })
        : await client.merchant[":merchantId"].tts["message-creators"].$post({
            param: scope.param,
            json: { message, creatorIds, conversationId, imageId },
          });
      if (!res.ok) throw await handleError(res);
      const data = await res.json();
      if (res.status === 202 && "async" in data && data.async) {
        return { async: true, jobId: data.jobId };
      }
      return { async: false, ...(data as MessageCreatorsResponseWithActivities) };
    },
    onSuccess: (data, { creatorIds }) => {
      if (data.async) return;
      // Update the activity cache with the new messages
      if (data.activities && data.activities.length > 0) {
        // Build a map of creatorId -> username from all filter variants of the creators cache
        // The query key is ["creators_with_activities", merchantId, filter], so we need to check multiple filters
        const creatorMap = new Map<string, string>();
        const leftPanelFilters: (ActivityFilter | undefined)[] = [
          "all",
          "messages",
          "creator_activity",
          "brand_activity",
          undefined,
        ];

        for (const filter of leftPanelFilters) {
          const creatorsCache = queryClientHook.getQueryData<
            InfiniteData<{ creators: Array<{ creatorId: string; username: string }> }>
          >(["creators_with_activities", scope.merchantId, filter]);

          creatorsCache?.pages.forEach((page) => {
            page.creators.forEach((c) => creatorMap.set(c.creatorId, c.username));
          });

          // Stop once we have a populated map
          if (creatorMap.size > 0) break;
        }

        // Update activity cache for each affected creator
        data.activities.forEach((activity) => {
          const username = creatorMap.get(activity.creatorId);
          if (!username) return;

          // Only update filters that should contain messages: "all", "messages", and undefined (no filter)
          // Do NOT update "activities" filter as it's for non-message activities only
          const filtersToUpdate: (RightPanelActivityFilter | undefined)[] = ["all", "messages", undefined];

          filtersToUpdate.forEach((filter) => {
            queryClientHook.setQueryData<InfiniteData<ActivitiesResponse>>(
              ["creator-activities", scope.merchantId, username, filter],
              (oldData) => {
                if (!oldData) return oldData;

                // Add the new activity to the BEGINNING of the first page
                // The UI reverses pages and activities, so first page's first activity ends up at bottom
                const newPages = [...oldData.pages];
                if (newPages.length > 0) {
                  newPages[0] = {
                    ...newPages[0],
                    activities: [activity, ...newPages[0].activities],
                  };
                } else {
                  // If no pages exist, create one
                  newPages.push({
                    activities: [activity],
                    hasMore: false,
                    nextCursor: undefined,
                  });
                }

                return {
                  ...oldData,
                  pages: newPages,
                };
              },
            );
          });
        });
      }

      // Invalidate creators list to update "last activity" timestamp
      queryClient.invalidateQueries({ queryKey: ["creators_with_activities", scope.merchantId] });

      // Also invalidate creator details for all affected creators
      for (const creatorId of creatorIds) {
        queryClient.invalidateQueries({ queryKey: ["creator", scope.merchantId, creatorId] });
      }
    },
  });
};

// ============================================================================
// Creator conversation messages
// ============================================================================

type CreatorMessagesPage = {
  conversationId: string;
  username: string;
  avatar: string;
  creatorSenderId: string;
  messages: TikTokMessage[];
  nextPageToken?: string;
};

/**
 * Fetch messages from a conversation with a specific creator with infinite scroll pagination
 */
export const useCreatorMessages = (creatorUsername: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["creator-messages", scope.merchantId, scope.workspaceId, creatorUsername],
    queryFn: async ({ pageParam }): Promise<CreatorMessagesPage> => {
      if (!scope.merchantId || !creatorUsername) {
        throw new Error("Missing merchantId or creatorUsername");
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts.creators[":creatorUsername"].messages.$get(
            {
              param: { ...scope.param, creatorUsername },
              query: { pageToken: pageParam },
            },
          )
        : await client.merchant[":merchantId"].tts.creators[":creatorUsername"].messages.$get({
            param: { ...scope.param, creatorUsername },
            query: { pageToken: pageParam },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled: !!scope.merchantId && !!creatorUsername,
  });
};

// ============================================================================
// Conversations list
// ============================================================================

type ConversationStatus = "ALL" | "UNREAD" | "UNREPLIED";

export type Conversation = {
  id: string;
  username: string;
  avatar: string;
  unread_count: number;
  creator_im_id: string;
};

type ConversationsPage = {
  conversations: Conversation[];
  nextPageToken?: string;
};

/**
 * Fetch list of conversations with optional status filter and infinite scroll pagination
 */
export const useConversations = (status?: ConversationStatus, options?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["conversations", scope.merchantId, scope.workspaceId, status],
    queryFn: async ({ pageParam }): Promise<ConversationsPage> => {
      if (!scope.merchantId) {
        throw new Error("Missing merchantId");
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts.conversations.$get({
            param: scope.param,
            query: { status, pageToken: pageParam },
          })
        : await client.merchant[":merchantId"].tts.conversations.$get({
            param: scope.param,
            query: { status, pageToken: pageParam },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled: options?.enabled !== undefined ? options.enabled && !!scope.merchantId : !!scope.merchantId,
  });
};

// ============================================================================
// Send message to conversation
// ============================================================================

type SendConversationMessagePayload = {
  conversationId: string;
  message: string;
  creatorId?: string;
};

/**
 * Send a message directly to a conversation by ID
 */
export const useSendConversationMessage = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      message,
      creatorId,
    }: SendConversationMessagePayload): Promise<{
      success: boolean;
      messageId: string;
    }> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts.conversations[
            ":conversationId"
          ].messages.$post({
            param: { ...scope.param, conversationId },
            json: { message, creatorId },
          })
        : await client.merchant[":merchantId"].tts.conversations[":conversationId"].messages.$post({
            param: { ...scope.param, conversationId },
            json: { message, creatorId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

// ============================================================================
// Creator activities
// ============================================================================

/**
 * Fetch activities for a specific creator by username with infinite scroll pagination
 */
export type RightPanelActivityFilter = "all" | "messages" | "activities";

export const useCreatorActivities = (creatorUsername: string | undefined, filter?: RightPanelActivityFilter) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["creator-activities", scope.merchantId, scope.workspaceId, creatorUsername, filter],
    queryFn: async ({ pageParam }): Promise<ActivitiesResponse> => {
      if (!scope.merchantId || !creatorUsername) {
        throw new Error("Missing merchantId or creatorUsername");
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts.creators[
            ":creatorUsername"
          ].activities.$get({
            param: { ...scope.param, creatorUsername },
            query: {
              cursor: pageParam,
              filter: filter,
            },
          })
        : await client.merchant[":merchantId"].tts.creators[":creatorUsername"].activities.$get({
            param: { ...scope.param, creatorUsername },
            query: {
              cursor: pageParam,
              filter: filter,
            },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: !!scope.merchantId && !!creatorUsername,
  });
};

/**
 * Fetch activities for a specific candidate by candidateId with infinite scroll pagination
 */
export const useCandidateActivities = (
  candidateId: string | undefined,
  filter: "all" | "messages" | "activities" = "all",
) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["candidate-activities", scope.merchantId, scope.workspaceId, candidateId, filter],
    queryFn: async ({ pageParam }): Promise<ActivitiesResponse> => {
      if (!scope.merchantId || !candidateId) {
        throw new Error("Missing merchantId or candidateId");
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].candidates[":candidateId"].activities.$get({
            param: { ...scope.param, candidateId },
            query: { cursor: pageParam, filter },
          })
        : await client.merchant[":merchantId"].candidates[":candidateId"].activities.$get({
            param: { ...scope.param, candidateId },
            query: { cursor: pageParam, filter },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: !!scope.merchantId && !!candidateId,
  });
};

// ============================================================================
// All Creator Activities (unified feed)
// ============================================================================

export type ActivityFilter = "all" | "messages" | "creator_activity" | "brand_activity";

// Separate type for /activities endpoint which doesn't support "messages" filter
// (only fetches DB activities, not TikTok messages)
export type DbActivityFilter = "all" | "creator_activity" | "brand_activity";

/**
 * Fetch activities for ALL creators with filtering and pagination
 * Note: This endpoint only fetches DB activities (videos, collabs, etc.)
 * For message activities, use useCreatorsWithActivities instead
 */
export const useAllCreatorActivities = (filter?: DbActivityFilter) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["all-creator-activities", scope.merchantId, scope.workspaceId, filter],
    queryFn: async ({ pageParam }): Promise<ActivitiesResponse> => {
      if (!scope.merchantId) {
        throw new Error("Missing merchantId");
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts.activities.$get({
            param: scope.param,
            query: {
              cursor: pageParam,
              filter: filter,
            },
          })
        : await client.merchant[":merchantId"].tts.activities.$get({
            param: scope.param,
            query: {
              cursor: pageParam,
              filter: filter,
            },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: !!scope.merchantId,
  });
};

// ============================================================================
// Creators with last activity (for Activities page)
// ============================================================================

export type CreatorWithActivity = {
  creatorId: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  lastActivity: CreatorActivity; // Always non-null since we only return creators with at least one activity
  type: "creator" | "candidate";
};

type CreatorsWithActivitiesResponse = {
  creators: CreatorWithActivity[];
  hasMore: boolean;
  nextCursor?: string;
};

/**
 * Fetch all creators with their last activity for the selected filter (paginated)
 */
export const useCreatorsWithActivities = (filter?: ActivityFilter, search?: string) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["creators_with_activities", scope.merchantId, scope.workspaceId, filter, search],
    queryFn: async ({ pageParam }): Promise<CreatorsWithActivitiesResponse> => {
      if (!scope.merchantId) {
        throw new Error("Missing merchantId");
      }

      const res = scope.workspaceId
        ? // @ts-expect-error - Type inference for new endpoints not picked up by TypeScript yet
          await client.workspace[":workspaceId"].merchant[":merchantId"].tts.creators_with_activities.$get({
            param: scope.param,
            query: {
              cursor: pageParam,
              filter: filter,
              search: search || undefined,
            },
          })
        : // @ts-expect-error - Type inference for new endpoints not picked up by TypeScript yet
          await client.merchant[":merchantId"].tts.creators_with_activities.$get({
            param: scope.param,
            query: {
              cursor: pageParam,
              filter: filter,
              search: search || undefined,
            },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: !!scope.merchantId,
  });
};
